import { ServerEvent } from "@/common/notification";
import { useMessage } from "@/common/messages";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { useWebchatCallRequestStore } from "@/common/stores/nonpersistingStores/webchatCallRequest";
import { useWebchatCustomerClosedStore } from "@/common/stores/nonpersistingStores/webchatCustomerClosed";
import { notificationListener } from "@/hooks/notification";
import { InteractionDto, InteractionOperations } from "@/repository/models";
import {
	WebchatVideoCallRequestChangedDto,
	WebchatVideoCallRequestStatus,
} from "@/repository/models/webchatCallRequest";
import { useIntl } from "react-intl";

export const useWebchatCallRequestManager = () => {
	const intl = useIntl();
	const { show: showMessage } = useMessage();
	const clearByRequestId = useWebchatCallRequestStore(
		(state) => state.clearByRequestId
	);
	const clearPending = useWebchatCallRequestStore(
		(state) => state.clearPending
	);
	const clearCustomerClosed = useWebchatCustomerClosedStore(
		(state) => state.clear
	);
	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const showRequestMessage = (conversationId: string, messageId: string) => {
		const namespace =
			currentInteraction?.id === conversationId ? "workspace" : "global";

		showMessage(namespace, {
			intent: "warning",
			text: intl.formatMessage({ id: messageId }),
		});
	};

	// Accepted is handled by WebchatVideoCallDialer (needs the ACS context,
	// which is only available below AcsProvider)
	notificationListener(
		ServerEvent.WebchatVideoCallRequestChanged,
		(data: WebchatVideoCallRequestChangedDto) => {
			if (
				data.status !== WebchatVideoCallRequestStatus.Declined &&
				data.status !== WebchatVideoCallRequestStatus.Expired &&
				data.status !== WebchatVideoCallRequestStatus.Cancelled
			) {
				return;
			}

			const trackedConversationId = clearByRequestId(data.requestId);

			if (!trackedConversationId) {
				return; // stale event or request initiated elsewhere
			}

			if (data.status === WebchatVideoCallRequestStatus.Declined) {
				showRequestMessage(
					data.conversationId,
					"Interaction.Messages.WebchatCallDeclined"
				);
			}

			if (data.status === WebchatVideoCallRequestStatus.Expired) {
				showRequestMessage(
					data.conversationId,
					"Interaction.Messages.WebchatCallExpired"
				);
			}
		}
	);

	notificationListener(
		ServerEvent.ActiveMessagingChanged,
		(interaction: InteractionDto) => {
			// Drop the tracked request once the interaction ends, or once the video call is active
			// (the agent answered) — from then on isOutboundAllowed governs the Start button.
			if (
				interaction.changeType === InteractionOperations.Removed ||
				interaction.hasActiveVideoCall
			) {
				clearPending(interaction.id);
			}

			if (interaction.changeType === InteractionOperations.Removed) {
				clearCustomerClosed(interaction.id);
			}
		}
	);
};
